const GRAIN_REFERENCE = {
    SOJA: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 18,
        idealDensity: 720,
        idealPh: 6.5
    },

    MILHO: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 25,
        idealDensity: 760,
        idealPh: 6.2
    },

    TRIGO: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 15,
        idealDensity: 780,
        idealPh: 6.0
    },

    ARROZ: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 24,
        idealDensity: 580,
        idealPh: 6.0
    },

    FEIJAO_CARIOCA: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 20,
        idealDensity: 770,
        idealPh: 6.5
    },

    FEIJAO_PRETO: {
        storageHumidity: 13,
        shippingHumidity: 14,
        harvestHumidity: 20,
        idealDensity: 790,
        idealPh: 6.5
    }
};

export const GRAIN_OPERATIONS = [
    { id: "COLHEITA", label: "Colheita" },
    { id: "RECEBIMENTO", label: "Recebimento" },
    { id: "ARMAZENAGEM", label: "Armazenagem" },
    { id: "EXPEDICAO", label: "Expedição" },
];

export const DEFAULT_GRAIN_OPERATION = "ARMAZENAGEM";

const OPERATION_LABELS = {
    COLHEITA: "Colheita",
    RECEBIMENTO: "Recebimento",
    ARMAZENAGEM: "Armazenagem",
    EXPEDICAO: "Expedição",
};

function stripAccents(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function normalizeOperation(operation) {
    if (!operation) return DEFAULT_GRAIN_OPERATION;

    const normalized = stripAccents(String(operation).trim()).toUpperCase();
    const aliases = {
        COLHEITA: "COLHEITA",
        RECEBIMENTO: "RECEBIMENTO",
        ARMAZENAGEM: "ARMAZENAGEM",
        ARMAZENAMENTO: "ARMAZENAGEM",
        EXPEDICAO: "EXPEDICAO",
    };

    if (aliases[normalized]) return aliases[normalized];
    if (OPERATION_LABELS[normalized]) return normalized;

    const byLabel = GRAIN_OPERATIONS.find(
        (item) =>
            stripAccents(item.label).toUpperCase() === normalized ||
            item.label.toLowerCase() === String(operation).trim().toLowerCase()
    );
    if (byLabel) return byLabel.id;

    return DEFAULT_GRAIN_OPERATION;
}

function calculateHumidityScore(humidity, ref, operation) {
    const { storageHumidity, shippingHumidity, harvestHumidity } = ref;

    switch (operation) {
        case "COLHEITA":
            if (humidity <= harvestHumidity * 0.85) return 100;
            if (humidity <= harvestHumidity) return 85;
            if (humidity <= harvestHumidity * 1.15) return 45;
            return 10;

        case "RECEBIMENTO":
            if (humidity <= shippingHumidity) return 100;
            if (humidity <= harvestHumidity) return 65;
            if (humidity <= harvestHumidity * 1.1) return 35;
            return 10;

        case "EXPEDICAO":
            if (humidity <= shippingHumidity) return 100;
            if (humidity <= shippingHumidity + 0.5) return 75;
            if (humidity <= harvestHumidity) return 40;
            return 10;

        case "ARMAZENAGEM":
        default:
            if (humidity <= storageHumidity) return 100;
            if (humidity <= shippingHumidity) return 75;
            if (humidity <= harvestHumidity) return 45;
            return 10;
    }
}

function calculateTemperatureScore(temp, operation) {
    if (operation === "COLHEITA") {
        if (temp <= 28) return 100;
        if (temp <= 32) return 85;
        if (temp <= 38) return 55;
        return 15;
    }

    if (temp <= 25) return 100;
    if (temp <= 30) return 90;
    if (temp <= 35) return 70;
    if (temp <= 40) return 40;
    return 10;
}

function calculateDensityScore(density, idealDensity) {
    const diff = Math.abs(idealDensity - density);

    return Math.max(
        0,
        Math.round(100 - (diff / idealDensity) * 100)
    );
}

function calculatePhScore(ph, idealPh) {
    if (!ph) return 100;

    const diff = Math.abs(ph - idealPh);

    return Math.max(
        0,
        Math.round(100 - (diff * 30))
    );
}

function levelFromScore(score) {
    if (score >= 80) return "NORMAL";
    if (score >= 50) return "ATENÇÃO";
    return "CRÍTICO";
}

function normalizeGrain(grain) {
    const raw = String(grain || "")
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

    if (!raw) return raw;
    if (raw.includes("SOJA")) return "SOJA";
    if (raw.includes("MILHO")) return "MILHO";
    if (raw.includes("TRIGO")) return "TRIGO";
    if (raw.includes("ARROZ")) return "ARROZ";
    if (raw.includes("FEIJAO PRETO") || raw.includes("FEIJAO_PRETO")) return "FEIJAO_PRETO";
    if (raw.includes("FEIJAO")) return "FEIJAO_CARIOCA";

    return raw.replace(/\s+/g, "_");
}

function resolveDensityAndPh(data) {
    const explicitDensity = Number(data.density);
    const explicitPh = Number(data.ph);

    if (!Number.isNaN(explicitDensity) && explicitDensity > 0) {
        return {
            density: explicitDensity,
            ph: !Number.isNaN(explicitPh) && explicitPh > 0 && explicitPh <= 14
                ? explicitPh
                : undefined,
        };
    }

    const combined = explicitPh;
    if (!Number.isNaN(combined) && combined > 0) {
        if (combined > 50) {
            return { density: combined, ph: undefined };
        }
        return { density: 0, ph: combined };
    }

    return { density: 0, ph: undefined };
}

function determineStatus(humidity, ref, operation) {
    const { storageHumidity, shippingHumidity, harvestHumidity } = ref;

    switch (operation) {
        case "COLHEITA":
            if (humidity <= harvestHumidity * 0.9) return "APTO PARA COLHEITA";
            if (humidity <= harvestHumidity) return "COLHEITA COM ATENÇÃO";
            return "AGUARDAR SECAGEM NO CAMPO";

        case "RECEBIMENTO":
            if (humidity <= shippingHumidity) return "APTO PARA RECEBIMENTO";
            if (humidity <= harvestHumidity) return "RECEBER COM RESSALVAS";
            return "RECUSAR OU SECAR ANTES DO RECEBIMENTO";

        case "EXPEDICAO":
            if (humidity <= shippingHumidity) return "APTO PARA EXPEDIÇÃO";
            if (humidity <= harvestHumidity) return "SECAR ANTES DA EXPEDIÇÃO";
            return "RISCO ELEVADO — NÃO EXPEDIR";

        case "ARMAZENAGEM":
        default:
            if (humidity <= storageHumidity) return "APTO PARA ARMAZENAMENTO";
            if (humidity <= shippingHumidity) return "SECAR ANTES DE ARMAZENAR";
            if (humidity <= harvestHumidity) return "NECESSITA SECAGEM";
            return "RISCO ELEVADO";
    }
}

function fungusRisk(humidity, temperature, operation) {
    if (operation === "COLHEITA") {
        if (humidity > 22 && temperature > 32) return "ALTO";
        if (humidity > 18) return "MÉDIO";
        return "BAIXO";
    }

    if (operation === "RECEBIMENTO") {
        if (humidity > 15 && temperature > 30) return "ALTO";
        if (humidity > 14) return "MÉDIO";
        return "BAIXO";
    }

    if (humidity > 15 && temperature > 30) return "ALTO";
    if (humidity > 13) return "MÉDIO";
    return "BAIXO";
}

function deteriorationRisk(humidity, temperature, operation) {
    let score = 0;

    const humidityLimit = operation === "COLHEITA" ? 20
        : operation === "RECEBIMENTO" ? 15
            : 14;

    const tempLimit = operation === "COLHEITA" ? 38 : 35;

    if (humidity > humidityLimit) score += 50;
    if (temperature > tempLimit) score += 50;

    if (score >= 80) return "ALTO";
    if (score >= 40) return "MÉDIO";
    return "BAIXO";
}

function storageEstimateLabel(operation) {
    switch (operation) {
        case "COLHEITA":
            return "Estimativa pós-colheita";
        case "RECEBIMENTO":
            return "Estimativa pós-recebimento";
        case "EXPEDICAO":
            return "Condição para expedição";
        case "ARMAZENAGEM":
        default:
            return "Armazenamento estimado";
    }
}

function storageEstimate(humidity, operation) {
    if (operation === "COLHEITA") {
        return "Avaliar após secagem";
    }

    if (operation === "RECEBIMENTO") {
        if (humidity <= 14) return "3-6 meses (após estabilização)";
        if (humidity <= 16) return "1-3 meses";
        return "< 30 dias";
    }

    if (operation === "EXPEDICAO") {
        if (humidity <= 13) return "Pronto para trânsito";
        if (humidity <= 14) return "Expedir com monitoramento";
        return "Não recomendado expedir";
    }

    if (humidity <= 12) return "12+ meses";
    if (humidity <= 13) return "8-12 meses";
    if (humidity <= 14) return "3-6 meses";
    if (humidity <= 16) return "1-3 meses";
    return "< 30 dias";
}

function getScoreWeights(operation) {
    switch (operation) {
        case "COLHEITA":
            return { humidity: 0.45, temperature: 0.35, density: 0.15, ph: 0.05 };
        case "RECEBIMENTO":
            return { humidity: 0.42, temperature: 0.28, density: 0.2, ph: 0.1 };
        case "EXPEDICAO":
            return { humidity: 0.45, temperature: 0.2, density: 0.25, ph: 0.1 };
        case "ARMAZENAGEM":
        default:
            return { humidity: 0.4, temperature: 0.25, density: 0.25, ph: 0.1 };
    }
}

function buildAlerts(data, ref, operation, density, ph) {
    const alerts = [];
    const { storageHumidity, shippingHumidity, harvestHumidity } = ref;
    const humidity = data.humidity;

    switch (operation) {
        case "COLHEITA":
            if (humidity > harvestHumidity) {
                alerts.push("Umidade acima do limite de colheita");
            } else if (humidity > harvestHumidity * 0.95) {
                alerts.push("Umidade próxima ao limite de colheita");
            }
            if (data.temperature > 38) {
                alerts.push("Temperatura elevada no campo");
            }
            break;

        case "RECEBIMENTO":
            if (humidity > shippingHumidity) {
                alerts.push("Umidade acima do aceitável no recebimento");
            }
            if (humidity > harvestHumidity) {
                alerts.push("Lote acima da umidade de colheita");
            }
            if (data.temperature > 35) {
                alerts.push("Temperatura elevada na recepção");
            }
            break;

        case "EXPEDICAO":
            if (humidity > shippingHumidity) {
                alerts.push("Umidade acima do limite para expedição");
            }
            if (humidity > storageHumidity + 1) {
                alerts.push("Risco de perda de qualidade no transporte");
            }
            break;

        case "ARMAZENAGEM":
        default:
            if (humidity > storageHumidity) {
                alerts.push("Umidade acima do ideal para armazenagem");
            }
            break;
    }

    if (data.temperature > (operation === "COLHEITA" ? 38 : 35)) {
        if (!alerts.some((a) => a.includes("Temperatura"))) {
            alerts.push("Temperatura elevada");
        }
    }

    if (density > 0 && density < ref.idealDensity * 0.9) {
        alerts.push("Baixa densidade");
    }

    if (ph && Math.abs(ph - ref.idealPh) > 0.5) {
        alerts.push("pH fora do ideal");
    }

    return alerts;
}

function generateRecommendation(data, ref, operation) {
    const { storageHumidity, shippingHumidity, harvestHumidity } = ref;
    const humidity = data.humidity;

    switch (operation) {
        case "COLHEITA":
            if (humidity > harvestHumidity) {
                return "Aguardar secagem no campo antes da colheita.";
            }
            if (humidity > harvestHumidity * 0.9) {
                return "Monitorar umidade; colheita possível com atenção.";
            }
            if (data.temperature > 38) {
                return "Priorizar colheita em horários mais frescos.";
            }
            return "Condições favoráveis para colheita.";

        case "RECEBIMENTO":
            if (humidity > harvestHumidity) {
                return "Secar o lote antes de concluir o recebimento.";
            }
            if (humidity > shippingHumidity) {
                return "Receber com ressalvas e encaminhar para secagem.";
            }
            if (data.temperature > 35) {
                return "Resfriar ou aerar antes do armazenamento provisório.";
            }
            return "Lote apto para recebimento.";

        case "EXPEDICAO":
            if (humidity > shippingHumidity) {
                return "Secar antes da expedição para evitar perdas.";
            }
            if (humidity > storageHumidity) {
                return "Verificar estabilidade do lote antes de expedir.";
            }
            if (data.temperature > 35) {
                return "Aguardar redução de temperatura para expedição.";
            }
            return "Lote apto para expedição.";

        case "ARMAZENAGEM":
        default:
            if (humidity > harvestHumidity) {
                return "Secagem imediata recomendada.";
            }
            if (humidity > shippingHumidity) {
                return "Necessário processo de secagem antes do armazenamento.";
            }
            if (humidity > storageHumidity) {
                return "Ajustar umidade para nível de armazenagem seguro.";
            }
            if (data.temperature > 35) {
                return "Verificar ventilação ou aeração do silo/armazém.";
            }
            return "Lote em condições adequadas para armazenagem.";
    }
}

/** Texto explicativo do score geral (UI — tooltip). */
export function getScoreTooltipText(operation = DEFAULT_GRAIN_OPERATION) {
    const op = normalizeOperation(operation);
    const weights = getScoreWeights(op);
    const label = OPERATION_LABELS[op];
    const wH = Math.round(weights.humidity * 100);
    const wT = Math.round(weights.temperature * 100);
    const wD = Math.round(weights.density * 100);
    const wP = Math.round(weights.ph * 100);

    return (
        `Nota de 0 a 100 que resume a condição do lote na etapa ${label}. ` +
        `Combina as médias de umidade (${wH}%), temperatura (${wT}%), densidade (${wD}%) e pH (${wP}%), ` +
        `comparadas aos valores ideais da curva do grão. Quanto mais próximo do ideal, maior o score.`
    );
}

export function analyzeGrain(data) {
    const grain = normalizeGrain(data.grain);
    const operation = normalizeOperation(data.operation || data.process);

    const ref = GRAIN_REFERENCE[grain];

    if (!ref) {
        throw new Error(`Grão não suportado: ${data.grain}`);
    }

    const { density, ph } = resolveDensityAndPh(data);

    const humidityScore = calculateHumidityScore(data.humidity, ref, operation);
    const temperatureScore = calculateTemperatureScore(data.temperature, operation);
    const densityScore = calculateDensityScore(density, ref.idealDensity);
    const phScore = calculatePhScore(ph, ref.idealPh);

    const weights = getScoreWeights(operation);

    const score = Math.round(
        humidityScore * weights.humidity +
        temperatureScore * weights.temperature +
        densityScore * weights.density +
        phScore * weights.ph
    );

    const alerts = buildAlerts(
        { ...data, density, ph },
        ref,
        operation,
        density,
        ph
    );

    return {
        grain,
        operation,
        operationLabel: OPERATION_LABELS[operation],

        score,

        status: determineStatus(data.humidity, ref, operation),

        fungusRisk: fungusRisk(data.humidity, data.temperature, operation),

        deteriorationRisk: deteriorationRisk(
            data.humidity,
            data.temperature,
            operation
        ),

        estimatedStorage: storageEstimate(data.humidity, operation),

        estimatedStorageLabel: storageEstimateLabel(operation),

        alerts,

        recommendation: generateRecommendation(
            { ...data, density, ph },
            ref,
            operation
        ),

        health: {
            humidity: {
                value: data.humidity,
                unit: "%",
                level: levelFromScore(humidityScore),
            },
            temperature: {
                value: data.temperature,
                unit: "°C",
                level: levelFromScore(temperatureScore),
            },
            density: {
                value: density,
                unit: "kg/m³",
                level: levelFromScore(densityScore),
            },
            ph: {
                value: ph ?? null,
                unit: "",
                level: levelFromScore(phScore),
            },
        },
    };
}
